import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { TimeSlot, Availability } from '../../types';
import { HeroSlotCard } from './HeroSlotCard';
import { AltSlotCard } from './AltSlotCard';

interface AllSlotsDialogProps {
  slots: TimeSlot[];
  participantName: string | null;
  availability: Availability[];
  allParticipantNames: string[];
  expandedSlotKey: string | null;
  onToggleExpand: (key: string) => void;
  onClose: () => void;
  onSelectSlot: (slot: TimeSlot) => Promise<void>;
  selectingSlot: string | null;
}

export function AllSlotsDialog({
  slots,
  participantName,
  availability,
  allParticipantNames,
  expandedSlotKey,
  onToggleExpand,
  onClose,
  onSelectSlot,
  selectingSlot,
}: AllSlotsDialogProps) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  const totalParticipants = allParticipantNames.length;
  const slotKey = (slot: TimeSlot) => `${slot.utc_start}-${slot.utc_end}`;

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const Body = (
    <div className="space-y-2">
      {slots.map((slot, index) => {
        const key = slotKey(slot);
        if (index === 0) {
          return (
            <HeroSlotCard
              key={key}
              slot={slot}
              totalParticipants={totalParticipants}
              allParticipantNames={allParticipantNames}
              participantName={participantName}
              isOpen={expandedSlotKey === key}
              onToggle={() => onToggleExpand(key)}
            />
          );
        }
        return (
          <AltSlotCard
            key={key}
            slot={slot}
            rank={index + 1}
            totalParticipants={totalParticipants}
            allParticipantNames={allParticipantNames}
            participantName={participantName}
            availability={availability}
            isSelecting={selectingSlot === key}
            isOpen={expandedSlotKey === key}
            onToggle={() => onToggleExpand(key)}
            onSelect={onSelectSlot}
          />
        );
      })}
    </div>
  );

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-2xl border border-room-border bg-room-surface p-4"
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-room-text">
              {t('leaderboard.allMeetingTimes', { count: slots.length })}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-room-text-dim hover:bg-room-surface-2"
              aria-label={t('dialog.close')}
            >
              <X size={20} />
            </button>
          </div>
          {Body}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-room-border bg-room-surface p-6 shadow-xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-room-text">
            {t('leaderboard.allMeetingTimes', { count: slots.length })}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-room-text-dim hover:bg-room-surface-2"
            aria-label={t('dialog.close')}
          >
            <X size={20} />
          </button>
        </div>
        {Body}
      </motion.div>
    </div>
  );
}
